import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Fragment, createElement, type FormEvent, type ReactNode, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { StatusBadge, Surface, SurfaceHeader } from "../components/PortalPrimitives";
import { fetchResearchHistory, fetchResearchMemo, fetchResearchSeries } from "../lib/api";
import { useCan } from "../lib/session";
import type { ChipTone, ResearchMemoSeries } from "../lib/types";

type ResearchFilters = {
  q: string;
  ticker: string;
};

type CompanyGroup = {
  key: string;
  companyName: string;
  ticker: string | null;
  updatedAt: string;
  outputs: ResearchMemoSeries[];
};

type MemoBlock =
  | { kind: "heading"; level: number; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "unordered-list"; items: string[] }
  | { kind: "ordered-list"; items: string[] }
  | { kind: "quote"; text: string }
  | { kind: "code"; text: string }
  | { kind: "literal"; text: string }
  | { kind: "rule" }
  | { kind: "table"; headers: string[]; rows: string[][] };

const EMPTY_FILTERS: ResearchFilters = { q: "", ticker: "" };

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

const artifactLabels: Record<string, string> = {
  analyst_note: "Analyst note",
  counter_thesis: "Counter-thesis",
  final_ic_memo: "Final IC memo",
  ic_packet: "IC packet",
  primary_analysis: "Primary analysis",
  sell_side_validation: "Sell-side validation",
  working_thesis: "Working thesis",
};

const artifactPluralLabels: Record<string, string> = {
  analyst_note: "analyst notes",
  counter_thesis: "counter-theses",
  final_ic_memo: "final IC memos",
  ic_packet: "IC packets",
  primary_analysis: "primary analyses",
  sell_side_validation: "sell-side validations",
  working_thesis: "working theses",
};

function parseDate(value: string | null): Date | null {
  if (!value) return null;
  const parsed = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function displayDate(value: string | null): string {
  const parsed = parseDate(value);
  return parsed ? dateFormatter.format(parsed) : value || "Date unavailable";
}

function displayLabel(value: string | null, fallback = "Unavailable"): string {
  if (!value) return fallback;
  const normalized = value.replace(/[_-]+/g, " ").trim();
  return normalized
    ? `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`
    : fallback;
}

function artifactLabel(value: string): string {
  return artifactLabels[value] ?? displayLabel(value, "Research output");
}

function statusPresentation(value: string): { label: string; tone: ChipTone } {
  switch (value) {
    case "approved":
      return { label: "Approved", tone: "green" };
    case "review_required":
      return { label: "Analyst review", tone: "amber" };
    case "draft":
      return { label: "Draft", tone: "gray" };
    case "rejected":
      return { label: "Rejected", tone: "red" };
    case "superseded":
      return { label: "Superseded", tone: "gray" };
    default:
      return { label: displayLabel(value, "Status unavailable"), tone: "gray" };
  }
}

function timestamp(value: string): number {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function groupSeries(items: ResearchMemoSeries[]): CompanyGroup[] {
  const companies = new Map<string, ResearchMemoSeries[]>();
  for (const item of items) {
    const outputs = companies.get(item.company.issuer_id) ?? [];
    const existingIndex = outputs.findIndex((output) => output.artifact_type === item.artifact_type);
    if (existingIndex === -1) {
      outputs.push(item);
    } else if (
      timestamp(item.latest_artifact.updated_at)
      > timestamp(outputs[existingIndex].latest_artifact.updated_at)
    ) {
      outputs[existingIndex] = item;
    }
    companies.set(item.company.issuer_id, outputs);
  }

  return Array.from(companies.entries())
    .map(([key, rawOutputs]) => {
      const outputs = [...rawOutputs].sort(
        (left, right) => timestamp(right.latest_artifact.updated_at) - timestamp(left.latest_artifact.updated_at),
      );
      const lead = outputs[0];
      return {
        key,
        companyName: lead.company.company_name || "Company unavailable",
        ticker: lead.company.ticker,
        updatedAt: lead.latest_artifact.updated_at,
        outputs,
      };
    })
    .sort((left, right) => timestamp(right.updatedAt) - timestamp(left.updatedAt));
}

function tableCells(line: string): string[] {
  const cells: string[] = [];
  let cell = "";
  let inCode = false;
  const value = line.trim();

  for (let index = 0; index < value.length; index += 1) {
    const character = value[index];
    if (character === "\\" && value[index + 1] === "|") {
      cell += "|";
      index += 1;
      continue;
    }
    if (character === "`") {
      inCode = !inCode;
      cell += character;
      continue;
    }
    if (character === "|" && !inCode) {
      cells.push(cell.trim());
      cell = "";
      continue;
    }
    cell += character;
  }
  cells.push(cell.trim());
  if (cells[0] === "") cells.shift();
  if (cells.at(-1) === "") cells.pop();
  return cells;
}

function isTableDivider(line: string): boolean {
  const cells = tableCells(line);
  return cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}

function startsBlock(lines: string[], index: number): boolean {
  const line = lines[index]?.trim() ?? "";
  const next = lines[index + 1]?.trim() ?? "";
  return (
    !line
    || /^#{1,6}\s+/.test(line)
    || /^```/.test(line)
    || /^>\s?/.test(line)
    || /^[-*+]\s+/.test(line)
    || /^\d+[.)]\s+/.test(line)
    || /^([-*_])(?:\s*\1){2,}$/.test(line)
    || (line.includes("|") && isTableDivider(next))
  );
}

function parseMemoMarkdown(markdown: string): MemoBlock[] {
  const lines = markdown.replace(/\r\n?/g, "\n").split("\n");
  const blocks: MemoBlock[] = [];
  let index = 0;

  while (index < lines.length) {
    const raw = lines[index];
    const line = raw.trim();
    if (!line) {
      index += 1;
      continue;
    }

    const heading = /^(#{1,6})\s+(.+)$/.exec(line);
    if (heading) {
      blocks.push({ kind: "heading", level: heading[1].length, text: heading[2].trim() });
      index += 1;
      continue;
    }

    if (/^```/.test(line)) {
      const code: string[] = [];
      index += 1;
      while (index < lines.length && !/^```/.test(lines[index].trim())) {
        code.push(lines[index]);
        index += 1;
      }
      if (index < lines.length) index += 1;
      blocks.push({ kind: "code", text: code.join("\n") });
      continue;
    }

    if (/^([-*_])(?:\s*\1){2,}$/.test(line)) {
      blocks.push({ kind: "rule" });
      index += 1;
      continue;
    }

    if (/^>\s?/.test(line)) {
      const quote: string[] = [];
      while (index < lines.length && /^>\s?/.test(lines[index].trim())) {
        quote.push(lines[index].trim().replace(/^>\s?/, ""));
        index += 1;
      }
      blocks.push({ kind: "quote", text: quote.join(" ") });
      continue;
    }

    const unordered = /^[-*+]\s+(.+)$/.exec(line);
    if (unordered) {
      const items: string[] = [];
      while (index < lines.length) {
        const match = /^[-*+]\s+(.+)$/.exec(lines[index].trim());
        if (!match) break;
        items.push(match[1]);
        index += 1;
      }
      blocks.push({ kind: "unordered-list", items });
      continue;
    }

    const ordered = /^\d+[.)]\s+(.+)$/.exec(line);
    if (ordered) {
      const items: string[] = [];
      while (index < lines.length) {
        const match = /^\d+[.)]\s+(.+)$/.exec(lines[index].trim());
        if (!match) break;
        items.push(match[1]);
        index += 1;
      }
      blocks.push({ kind: "ordered-list", items });
      continue;
    }

    if (line.includes("|") && isTableDivider(lines[index + 1]?.trim() ?? "")) {
      const tableLines = [line, lines[index + 1].trim()];
      index += 2;
      while (index < lines.length && lines[index].trim().includes("|")) {
        tableLines.push(lines[index].trim());
        index += 1;
      }
      const headers = tableCells(tableLines[0]);
      const divider = tableCells(tableLines[1]);
      const rows = tableLines.slice(2).map(tableCells);
      const valid = (
        headers.length > 1
        && divider.length === headers.length
        && divider.every((cell) => /^:?-{3,}:?$/.test(cell))
        && rows.every((row) => row.length === headers.length)
      );
      blocks.push(valid
        ? { kind: "table", headers, rows }
        : { kind: "literal", text: tableLines.join("\n") });
      continue;
    }

    const paragraph = [line];
    index += 1;
    while (index < lines.length && !startsBlock(lines, index)) {
      paragraph.push(lines[index].trim());
      index += 1;
    }
    blocks.push({ kind: "paragraph", text: paragraph.join(" ") });
  }

  return blocks;
}

function safeHref(value: string): string | null {
  const href = value.trim();
  if (/^https?:\/\//i.test(href) || href.startsWith("#")) return href;
  if (href.startsWith("/") && !href.startsWith("//")) return href;
  return null;
}

function inlineMarkdown(text: string, keyPrefix: string): ReactNode[] {
  const pattern = /(\*\*[^*]+\*\*|__[^_]+__|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  const nodes: ReactNode[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;
  let part = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > cursor) nodes.push(text.slice(cursor, match.index));
    const token = match[0];
    const key = `${keyPrefix}-${part}`;
    if ((token.startsWith("**") && token.endsWith("**")) || (token.startsWith("__") && token.endsWith("__"))) {
      nodes.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith("`") && token.endsWith("`")) {
      nodes.push(<code key={key}>{token.slice(1, -1)}</code>);
    } else {
      const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);
      const href = link ? safeHref(link[2]) : null;
      nodes.push(
        href && link ? (
          <a key={key} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel={href.startsWith("http") ? "noopener noreferrer" : undefined}>
            {link[1]}
          </a>
        ) : (
          <Fragment key={key}>{token}</Fragment>
        ),
      );
    }
    cursor = match.index + token.length;
    part += 1;
  }
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes;
}

function MemoBody({ markdown }: { markdown: string }) {
  const blocks = useMemo(() => parseMemoMarkdown(markdown), [markdown]);
  if (blocks.length === 0) {
    return (
      <div className="research-reader-empty">
        <strong>This research output has no readable body.</strong>
        <span>Its title and status are still available above.</span>
      </div>
    );
  }

  return (
    <div className="research-memo-body">
      {blocks.map((block, index) => {
        const key = `memo-block-${index}`;
        if (block.kind === "heading") {
          const tag = `h${Math.min(block.level + 1, 6)}`;
          return createElement(tag, { key }, inlineMarkdown(block.text, key));
        }
        if (block.kind === "paragraph") return <p key={key}>{inlineMarkdown(block.text, key)}</p>;
        if (block.kind === "quote") return <blockquote key={key}>{inlineMarkdown(block.text, key)}</blockquote>;
        if (block.kind === "code") return <pre key={key}><code>{block.text}</code></pre>;
        if (block.kind === "literal") return <pre className="research-markdown-literal" key={key}>{block.text}</pre>;
        if (block.kind === "rule") return <hr key={key} />;
        if (block.kind === "unordered-list" || block.kind === "ordered-list") {
          const List = block.kind === "unordered-list" ? "ul" : "ol";
          return (
            <List key={key}>
              {block.items.map((item, itemIndex) => (
                <li key={`${key}-${itemIndex}`}>{inlineMarkdown(item, `${key}-${itemIndex}`)}</li>
              ))}
            </List>
          );
        }
        return (
          <div className="research-memo-table-wrap" key={key}>
            <table>
              <thead>
                <tr>{block.headers.map((header, cellIndex) => <th scope="col" key={`${key}-head-${cellIndex}`}>{inlineMarkdown(header, `${key}-head-${cellIndex}`)}</th>)}</tr>
              </thead>
              <tbody>
                {block.rows.map((row, rowIndex) => (
                  <tr key={`${key}-row-${rowIndex}`}>
                    {block.headers.map((_, cellIndex) => <td key={`${key}-cell-${rowIndex}-${cellIndex}`}>{inlineMarkdown(row[cellIndex] ?? "", `${key}-cell-${rowIndex}-${cellIndex}`)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

function ResearchState({
  title,
  detail,
  error = false,
  onRetry,
}: {
  title: string;
  detail: string;
  error?: boolean;
  onRetry?: () => void;
}) {
  return (
    <Surface className={`research-state${error ? " research-state--error" : ""}`} role={error ? "alert" : "status"} aria-live="polite">
      <div>
        <strong>{title}</strong>
        <span>{detail}</span>
      </div>
      {onRetry ? <button className="btn" type="button" onClick={onRetry}>Try again</button> : null}
    </Surface>
  );
}

function CompanyResearch({ company }: { company: CompanyGroup }) {
  const headingId = `research-company-${encodeURIComponent(company.key)}`;
  return (
    <section className="research-company" aria-labelledby={headingId}>
      <header className="research-company-head">
        <h3 id={headingId}>
          <strong>{company.ticker || "—"}</strong>
          <span>{company.companyName}</span>
        </h3>
        <span>{company.outputs.length} current {company.outputs.length === 1 ? "output" : "outputs"}</span>
      </header>
      <ul className="research-output-list">
        {company.outputs.map((series) => {
          const current = series.latest_artifact;
          const status = statusPresentation(current.status);
          const approved = series.latest_approved_artifact?.artifact_id !== current.artifact_id
            ? series.latest_approved_artifact
            : null;
          const historyLabel = artifactPluralLabels[series.artifact_type] ?? "research runs";
          return (
            <li className="research-output" key={series.artifact_type}>
              <time className="research-output-date" dateTime={current.updated_at}>{displayDate(current.updated_at)}</time>
              <div className="research-output-copy">
                <span>{artifactLabel(series.artifact_type)}</span>
                <strong>{current.title || artifactLabel(series.artifact_type)}</strong>
                <div className="research-output-meta">
                  <span>{series.run_count} {series.run_count === 1 ? "run" : "runs"}</span>
                  <Link
                    to={`/research/${current.artifact_id}/history`}
                    aria-label={`View ${historyLabel} history for ${company.companyName}`}
                  >
                    View {historyLabel} history
                  </Link>
                  {approved ? (
                    <Link
                      to={`/research/${approved.artifact_id}`}
                      aria-label={`Read latest approved ${artifactLabel(series.artifact_type)} for ${company.companyName}`}
                    >
                      Read latest approved
                    </Link>
                  ) : null}
                </div>
              </div>
              <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
              <Link
                className="btn research-read-action"
                to={`/research/${current.artifact_id}`}
                aria-label={`Read latest ${artifactLabel(current.artifact_type)} for ${company.companyName}`}
              >
                Read latest
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function errorStatus(error: unknown): number | null {
  if (!error || typeof error !== "object" || !("status" in error)) return null;
  const status = (error as { status?: unknown }).status;
  return typeof status === "number" ? status : null;
}

function ResearchHistory({ artifactId }: { artifactId: string }) {
  const history = useInfiniteQuery({
    queryKey: ["research-history", artifactId],
    queryFn: ({ pageParam }) => fetchResearchHistory(artifactId, { cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    retry: false,
  });

  if (history.isLoading) {
    return <ResearchState title="Loading research history" detail="Preparing one row for each logical research run…" />;
  }

  if (history.isError && !history.data) {
    const notFound = errorStatus(history.error) === 404;
    return (
      <div className="research-detail-state">
        <ResearchState
          title={notFound ? "Research history not found" : "Research history could not be loaded"}
          detail={notFound ? "The series link may be out of date or no longer available." : "Retry the history request or return to the research library."}
          error
          onRetry={notFound ? undefined : () => void history.refetch()}
        />
        <Link className="research-return" to="/research">Return to Research</Link>
      </div>
    );
  }

  const pages = history.data?.pages;
  const firstPage = pages?.[0];
  if (!firstPage) return null;
  const seen = new Set<string>();
  const runs = pages.flatMap((page) => page.runs).filter((run) => {
    const id = run.latest_artifact.artifact_id;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
  const outputLabel = artifactLabel(firstPage.artifact_type);
  const pluralLabel = artifactPluralLabels[firstPage.artifact_type] ?? "research outputs";
  const companyName = firstPage.company.company_name || "Company unavailable";

  return (
    <div className="research-history-page">
      <header className="research-history-head">
        <div>
          <nav className="research-breadcrumb" aria-label="Breadcrumb">
            <Link to="/research">Research</Link>
            <span aria-hidden="true">/</span>
            <span>{outputLabel} history</span>
          </nav>
          <div className="research-eyebrow">Logical research runs</div>
          <h1>{companyName} — {outputLabel} history</h1>
          <p>Each row is one research run. Revisions within the same run do not inflate the count.</p>
        </div>
        <div className="research-history-company">
          <strong>{firstPage.company.ticker || "—"}</strong>
          <StatusBadge tone="blue">{firstPage.total_count} {firstPage.total_count === 1 ? "run" : "runs"}</StatusBadge>
        </div>
      </header>

      <Surface className="research-history-surface" aria-labelledby="research-history-title" aria-busy={history.isFetching}>
        <SurfaceHeader
          title={`All ${pluralLabel}`}
          titleId="research-history-title"
          detail="Newest research run first; 25 at a time."
          count={firstPage.total_count}
        />
        <div className="research-results-summary" aria-live="polite">
          Showing {runs.length} of {firstPage.total_count} {firstPage.total_count === 1 ? "run" : "runs"}
        </div>
        {runs.length === 0 ? (
          <div className="research-empty">
            <strong>No research runs are available</strong>
            <span>This output series does not yet contain a readable run.</span>
          </div>
        ) : (
          <ol className="research-history-list">
            {runs.map((run, runIndex) => {
              const artifact = run.latest_artifact;
              const status = statusPresentation(artifact.status);
              const title = artifact.title || outputLabel;
              return (
                <li className="research-history-run" key={artifact.artifact_id}>
                  <time dateTime={run.requested_at}>{displayDate(run.requested_at)}</time>
                  <div className="research-history-copy">
                    <span>{run.request_type_label} · {run.depth_label}</span>
                    <strong>{title}</strong>
                    {run.completed_at || run.version_count > 1 ? (
                      <small>
                        {run.completed_at ? <><span>Completed </span><time dateTime={run.completed_at}>{displayDate(run.completed_at)}</time></> : null}
                        {run.completed_at && run.version_count > 1 ? " · " : null}
                        {run.version_count > 1 ? `${run.version_count} revisions in this run` : null}
                      </small>
                    ) : null}
                  </div>
                  <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
                  <Link
                    className="btn research-history-read"
                    to={`/research/${artifact.artifact_id}`}
                    aria-label={`Read run ${runIndex + 1}: ${title}, requested ${displayDate(run.requested_at)} (${run.request_type_label})`}
                  >
                    Read {outputLabel}
                  </Link>
                </li>
              );
            })}
          </ol>
        )}

        {history.isFetchNextPageError ? (
          <div className="research-history-more-error" role="alert">
            <span>More runs could not be loaded. The runs already shown are unaffected.</span>
            <button className="btn" type="button" onClick={() => void history.fetchNextPage()}>Try loading more again</button>
          </div>
        ) : null}
        {history.hasNextPage && !history.isFetchNextPageError ? (
          <div className="research-history-more">
            <button
              className="btn"
              type="button"
              disabled={history.isFetchingNextPage}
              onClick={() => void history.fetchNextPage()}
            >
              {history.isFetchingNextPage ? "Loading more…" : "Load 25 more runs"}
            </button>
          </div>
        ) : null}
      </Surface>
    </div>
  );
}

function ResearchDetail({ artifactId }: { artifactId: string }) {
  const canExport = useCan("research.export");
  const detail = useQuery({
    queryKey: ["research-memo", artifactId],
    queryFn: () => fetchResearchMemo(artifactId),
    retry: false,
  });

  if (detail.isLoading) {
    return <ResearchState title="Loading research" detail="Preparing the memo for reading…" />;
  }

  if (detail.isError || !detail.data) {
    const notFound = errorStatus(detail.error) === 404;
    return (
      <div className="research-detail-state">
        <ResearchState
          title={notFound ? "Research memo not found" : "Research memo could not be loaded"}
          detail={notFound ? "The link may be out of date or the memo may have been removed." : "Retry the memo request or return to the research library."}
          error
          onRetry={notFound ? undefined : () => void detail.refetch()}
        />
        <Link className="research-return" to="/research">Return to Research</Link>
      </div>
    );
  }

  const { memo, versions } = detail.data;
  const status = statusPresentation(memo.status);
  const earlierVersions = versions.filter((version) => version.artifact_id !== memo.artifact_id);
  const pdfLabel = memo.status === "approved" ? "Download PDF" : "Download review PDF";

  return (
    <div className="research-detail-page">
      <header className="research-detail-head">
        <div className="research-detail-heading">
          <nav className="research-breadcrumb" aria-label="Breadcrumb">
            <Link to="/research">Research</Link>
            <span aria-hidden="true">/</span>
            <span>Memo</span>
          </nav>
          <div className="research-detail-eyebrow">
            <span>{artifactLabel(memo.artifact_type)}</span>
            <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
          </div>
          <h1>{memo.title || artifactLabel(memo.artifact_type)}</h1>
          <div className="research-detail-summary">
            <strong>{memo.ticker || "—"}</strong>
            <span>{memo.company_name || "Company unavailable"}</span>
            <time dateTime={memo.updated_at}>Updated {displayDate(memo.updated_at)}</time>
          </div>
        </div>
        {memo.issuer_id || memo.has_pdf ? <div className="research-detail-actions">
          {memo.issuer_id ? <Link className="btn" to={`/research/${memo.artifact_id}/history`}>View run history</Link> : null}
          {memo.has_pdf ? (
            canExport ? (
              <a className="btn" href={`/api/portal/research/${memo.artifact_id}/pdf`}>{pdfLabel}</a>
            ) : (
              <span className="research-pdf-note">PDF download isn’t available for your access level.</span>
            )
          ) : null}
        </div> : null}
      </header>

      <div className={`research-detail-layout${earlierVersions.length > 0 ? " research-detail-layout--versions" : ""}`}>
        <Surface className="research-reader" aria-labelledby="research-reader-title">
          <SurfaceHeader
            title="Memo"
            titleId="research-reader-title"
            detail={`${artifactLabel(memo.artifact_type)} · ${status.label}`}
          />
          <MemoBody markdown={memo.content_markdown} />
        </Surface>

        {earlierVersions.length > 0 ? (
          <Surface className="research-versions" aria-labelledby="research-versions-title">
            <SurfaceHeader title="Earlier versions" titleId="research-versions-title" count={earlierVersions.length} />
            <nav aria-label="Earlier memo versions">
              {earlierVersions.map((version) => {
                const versionStatus = statusPresentation(version.status);
                return (
                  <Link key={version.artifact_id} to={`/research/${version.artifact_id}`}>
                    <span>Version {version.version}</span>
                    <time dateTime={version.updated_at}>{displayDate(version.updated_at)}</time>
                    <StatusBadge tone={versionStatus.tone}>{versionStatus.label}</StatusBadge>
                  </Link>
                );
              })}
            </nav>
          </Surface>
        ) : null}
      </div>
    </div>
  );
}

export default function Research() {
  const { artifactId, historyArtifactId } = useParams();
  const [draft, setDraft] = useState<ResearchFilters>(EMPTY_FILTERS);
  const [active, setActive] = useState<ResearchFilters>(EMPTY_FILTERS);
  const seriesQuery = useInfiniteQuery({
    queryKey: ["research-series", active],
    queryFn: ({ pageParam }) => fetchResearchSeries({
      ...(active.q ? { q: active.q } : {}),
      ...(active.ticker ? { ticker: active.ticker } : {}),
      ...(pageParam ? { cursor: pageParam } : {}),
    }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    enabled: !artifactId && !historyArtifactId,
    retry: false,
  });

  if (historyArtifactId) return <ResearchHistory artifactId={historyArtifactId} />;
  if (artifactId) return <ResearchDetail artifactId={artifactId} />;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setActive({ q: draft.q.trim(), ticker: draft.ticker.trim().toUpperCase() });
  }

  function clearFilters() {
    setDraft(EMPTY_FILTERS);
    setActive(EMPTY_FILTERS);
  }

  const series = seriesQuery.data?.pages.flatMap((page) => page.series) ?? [];
  const companies = groupSeries(series);
  const currentOutputCount = companies.reduce((total, company) => total + company.outputs.length, 0);
  const activeFilterCount = [active.q, active.ticker].filter(Boolean).length;

  return (
    <div className="research-page">
      <header className="research-pagehead">
        <div>
          <div className="research-eyebrow">Recent research outputs</div>
          <h1>Research</h1>
          <p>Find the latest memo or packet for each company, then open it and read it immediately.</p>
        </div>
        <div className="research-page-actions">
          {seriesQuery.data ? <StatusBadge tone="green">{companies.length} {companies.length === 1 ? "company" : "companies"}</StatusBadge> : null}
          <button className="btn" type="button" disabled={seriesQuery.isFetching} onClick={() => void seriesQuery.refetch()}>
            {seriesQuery.isFetching && !seriesQuery.isLoading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </header>

      <Surface className="research-filters">
        <form className="research-filter-form" aria-label="Research filters" onSubmit={submit}>
          <label>
            <span>Search</span>
            <input
              value={draft.q}
              onChange={(event) => setDraft({ ...draft, q: event.target.value })}
              placeholder="Memo title or company"
              aria-label="Search research"
            />
          </label>
          <label>
            <span>Ticker</span>
            <input
              value={draft.ticker}
              onChange={(event) => setDraft({ ...draft, ticker: event.target.value })}
              placeholder="e.g. TTD"
              autoCapitalize="characters"
              aria-label="Filter research by ticker"
            />
          </label>
          <div className="research-filter-actions">
            <button className="btn primary" type="submit" disabled={seriesQuery.isFetching}>Filter</button>
            <button
              className="btn"
              type="button"
              disabled={activeFilterCount === 0 && !draft.q && !draft.ticker}
              onClick={clearFilters}
            >
              Clear
            </button>
          </div>
        </form>
      </Surface>

      {seriesQuery.isLoading ? <ResearchState title="Loading research" detail="Checking the newest memo and packet series…" /> : null}
      {seriesQuery.isError && !seriesQuery.data ? (
        <ResearchState
          title="Research could not be loaded"
          detail="The rest of Athena is unaffected. Retry the research request."
          error
          onRetry={() => void seriesQuery.refetch()}
        />
      ) : null}

      {seriesQuery.data ? (
        <Surface className="research-library" aria-labelledby="research-library-title" aria-busy={seriesQuery.isFetching}>
          <SurfaceHeader
            title="Latest research by company"
            titleId="research-library-title"
            detail={activeFilterCount > 0 ? `${activeFilterCount} active ${activeFilterCount === 1 ? "filter" : "filters"}` : "One row per company and output type; run histories are paginated."}
            count={currentOutputCount}
          />
          <div className="research-results-summary" aria-live="polite">
            {companies.length} {companies.length === 1 ? "company" : "companies"} · {currentOutputCount} current {currentOutputCount === 1 ? "output" : "outputs"}
          </div>
          {seriesQuery.isRefetchError && !seriesQuery.isFetchNextPageError ? (
            <div className="research-history-more-error" role="alert">
              <span>Research could not be refreshed. The results already shown are unaffected.</span>
              <button className="btn" type="button" onClick={() => void seriesQuery.refetch()}>Try refreshing again</button>
            </div>
          ) : null}
          {companies.length === 0 ? (
            <div className="research-empty">
              <strong>{activeFilterCount > 0 ? "No research matches these filters" : "No research outputs are available yet"}</strong>
              <span>{activeFilterCount > 0 ? "Try a broader company name, memo title, or ticker." : "New memo and packet outputs will appear here."}</span>
              {activeFilterCount > 0 ? <button className="btn" type="button" onClick={clearFilters}>Clear filters</button> : null}
            </div>
          ) : (
            <div className="research-company-list">
              {companies.map((company) => <CompanyResearch key={company.key} company={company} />)}
            </div>
          )}
          {seriesQuery.isFetchNextPageError ? (
            <div className="research-history-more-error" role="alert">
              <span>More research series could not be loaded. The results already shown are unaffected.</span>
              <button className="btn" type="button" onClick={() => void seriesQuery.fetchNextPage()}>Try loading more again</button>
            </div>
          ) : null}
          {seriesQuery.hasNextPage && !seriesQuery.isFetchNextPageError ? (
            <div className="research-history-more">
              <button
                className="btn"
                type="button"
                disabled={seriesQuery.isFetchingNextPage}
                onClick={() => void seriesQuery.fetchNextPage()}
              >
                {seriesQuery.isFetchingNextPage ? "Loading more…" : "Load more companies"}
              </button>
            </div>
          ) : null}
        </Surface>
      ) : null}
    </div>
  );
}
